import { Link, createFileRoute, notFound } from "@tanstack/react-router";
import { ExternalLink } from "lucide-react";

import {
  CihArticleRows,
  type ArticleRowData,
} from "~/components/cih/article-rows";
import {
  CihAtAGlanceCard,
  type AtAGlanceRow,
  type SocialLink,
} from "~/components/cih/at-a-glance-card";
import { CihClosingCta } from "~/components/cih/closing-cta";
import { CihCoachGrid } from "~/components/cih/coach-grid";
import { CihFaqSection } from "~/components/cih/faq-section";
import { CihFrameworkCards } from "~/components/cih/framework-cards";
import { CihHero } from "~/components/cih/hero";
import {
  CihProgramRows,
  type ProgramRowData,
} from "~/components/cih/program-rows";
import { CihPullQuoteBand } from "~/components/cih/pull-quote-band";
import { RelatedEntityPills } from "~/components/cih/related-entity-pills";
import { CihSection } from "~/components/cih/section";
import { CihSectionHeader } from "~/components/cih/section-header";
import { CihStatStrip } from "~/components/cih/stat-strip";
import { CihTabNav, type CihTab } from "~/components/cih/tab-nav";
import { FacebookIcon } from "~/components/icons/facebook-icon";
import { InstagramIcon } from "~/components/icons/instagram-icon";
import { LinkedInIcon } from "~/components/icons/linkedin-icon";
import { XIcon } from "~/components/icons/x-icon";
import { YouTubeIcon } from "~/components/icons/youtube-icon";
import { Button, ButtonLink } from "~/components/ui/button";
import { RichTextViewer } from "~/components/ui/rich-text-editor";
import { CCO_TYPE_DISPLAY_NAME_SINGULAR } from "~/lib/ccos";
import {
  getCcoArticlesFn,
  getChallengesByCcoFn,
  getPublishedCcoWithRelationsFn,
  isCcoArchivedFn,
  type CcoArticleRow,
  type CcoWithRelations,
} from "~/lib/functions/cih/ccos";
import { markResponseGone } from "~/lib/mark-response-gone";
import type { CCOType } from "~/lib/schemas/db-enums";
import { dropRestatedOpeningHeading } from "~/lib/shared/cih/body-toc";
import { buildStatStrip } from "~/lib/shared/cih/stat-strip";
import {
  createBrandMeta,
  createBreadcrumbStructuredData,
  createFaqStructuredData,
  createOrganizationStructuredData,
} from "~/lib/shared/meta";

type ChallengeLink = Awaited<ReturnType<typeof getChallengesByCcoFn>>[number];

type LoaderData = {
  title: string;
  cco: CcoWithRelations["cco"];
  coaches: CcoWithRelations["coaches"];
  ctps: CcoWithRelations["ctps"];
  frameworks: CcoWithRelations["frameworks"];
  challenges: ChallengeLink[];
  articles: CcoArticleRow[];
  yearsActive: number | null;
};

/** Safely parse a URL hostname; returns null when the input is not a valid URL. */
function safeHostname(url: string | null): string | null {
  if (!url) return null;
  try {
    return new URL(url).hostname;
  } catch {
    return null;
  }
}

const SOCIAL_ICONS = {
  linkedin: { Icon: LinkedInIcon, label: "LinkedIn" },
  instagram: { Icon: InstagramIcon, label: "Instagram" },
  youtube: { Icon: YouTubeIcon, label: "YouTube" },
  facebook: { Icon: FacebookIcon, label: "Facebook" },
  twitter: { Icon: XIcon, label: "Twitter" },
} as const;

export const Route = createFileRoute("/_public/organizations/$slug/")({
  staticData: { title: "Organization" },
  loader: async ({ params }): Promise<LoaderData> => {
    const data = await getPublishedCcoWithRelationsFn({
      data: { slug: params.slug },
    });
    if (!data) {
      // An organization we deliberately withdrew keeps its crawled URL alive in
      // Site Audit for as long as it answers 404 ("try again later"). 410 tells
      // crawlers to drop it for good. Anything else — a typo, a never-published
      // draft — stays a 404, since the public slug space is unbounded and most
      // misses were never real pages. The extra lookup only runs on a miss.
      if (await isCcoArchivedFn({ data: { slug: params.slug } })) {
        await markResponseGone();
      }
      throw notFound();
    }
    const [challenges, articles] = await Promise.all([
      getChallengesByCcoFn({ data: { ccoId: data.cco.id } }),
      getCcoArticlesFn({ data: { ccoId: data.cco.id } }),
    ]);
    return {
      title: data.cco.name,
      cco: data.cco,
      coaches: data.coaches,
      ctps: data.ctps,
      frameworks: data.frameworks,
      challenges,
      articles,
      yearsActive: data.cco.foundedYear
        ? new Date().getUTCFullYear() - data.cco.foundedYear
        : null,
    };
  },
  head: ({
    loaderData,
    params,
  }: {
    loaderData?: LoaderData;
    params: { slug: string };
  }) => {
    const name = loaderData?.cco.name ?? params.slug;
    const url = `https://dreamcoachmatch.com/organizations/${params.slug}`;
    const base = createBrandMeta({
      title: `${name} | Coaching Organizations`,
      description:
        loaderData?.cco.description || `Discover coaches from ${name}.`,
      ogUrl: url,
      ogImage: loaderData?.cco.logoUrl ?? undefined,
      canonical: url,
    });
    if (!loaderData) return base;

    const cco = loaderData.cco;
    const socialUrls = cco.socialLinks
      ? (Object.values(cco.socialLinks).filter(
          (v): v is string => typeof v === "string" && v.trim().length > 0,
        ) as string[])
      : [];

    const orgLd = createOrganizationStructuredData({
      name: cco.name,
      url,
      description: cco.description ?? undefined,
      logo: cco.logoUrl ?? undefined,
      foundedYear: cco.foundedYear ?? undefined,
      founderName: cco.founder ?? undefined,
      addressCountry: cco.headquartersCountry ?? undefined,
      sameAs: socialUrls,
    });
    const breadcrumbLd = createBreadcrumbStructuredData([
      { name: "Home", url: "https://dreamcoachmatch.com" },
      {
        name: "Organizations",
        url: "https://dreamcoachmatch.com/organizations",
      },
      { name: cco.name, url },
    ]);
    const faqLd =
      (cco.faqs?.length ?? 0) > 0
        ? createFaqStructuredData(cco.faqs ?? [])
        : null;

    return {
      ...base,
      scripts: [
        { type: "application/ld+json", children: JSON.stringify(orgLd) },
        {
          type: "application/ld+json",
          children: JSON.stringify(breadcrumbLd),
        },
        ...(faqLd
          ? [
              {
                type: "application/ld+json",
                children: JSON.stringify(faqLd),
              },
            ]
          : []),
      ],
    };
  },
  component: OrganizationDetailPage,
});

function OrganizationDetailPage() {
  const { cco, coaches, ctps, frameworks, challenges, articles, yearsActive } =
    Route.useLoaderData();
  const { slug } = Route.useParams();

  const challengePills = challenges
    .filter((c): c is ChallengeLink & { slug: string } => c.slug != null)
    .map((c) => ({ label: c.title, href: `/coaching-for/${c.slug}` }));

  const articleRows: ArticleRowData[] = articles.map((a) => ({
    id: a.id,
    slug: a.slug,
    title: a.title,
    description: a.description,
    authorName: a.coachFullName,
    challengeTitle: a.challengeTitle,
    challengeSlug: a.challengeSlug,
  }));

  const websiteHost = safeHostname(cco.website);
  const typeLabel =
    CCO_TYPE_DISPLAY_NAME_SINGULAR[cco.type as CCOType] ?? cco.type;

  const atAGlanceRows: AtAGlanceRow[] = [
    { key: "Type", value: typeLabel },
    {
      key: "Founded",
      value: cco.foundedYear ? String(cco.foundedYear) : null,
    },
    { key: "Headquarters", value: cco.headquartersCountry },
    { key: "Founder", value: cco.founder, href: cco.founderUrl ?? undefined },
    {
      key: "Website",
      value: websiteHost,
      href: cco.website ?? undefined,
    },
  ];

  const statCells = buildStatStrip({
    narrativeStats: cco.narrativeStats,
    hidePlatformStats: cco.hidePlatformStats,
    derivedStats: [
      {
        value: ctps.length > 0 ? String(ctps.length) : null,
        label: "Training programs",
      },
      {
        value: frameworks.length > 0 ? String(frameworks.length) : null,
        label: "Frameworks originated",
      },
      {
        value: yearsActive !== null ? `${yearsActive}y` : null,
        label: "Years active",
      },
    ],
  });

  const faqs = cco.faqs ?? [];

  const tabs: CihTab[] = [
    { id: "overview", label: "Overview", count: cco.body ? undefined : 0 },
    { id: "mission", label: "Mission", count: cco.pullQuote ? undefined : 0 },
    { id: "frameworks", label: "Frameworks", count: frameworks.length },
    { id: "programs", label: "Programs", count: ctps.length },
    { id: "challenges", label: "Challenges", count: challengePills.length },
    {
      id: "coaches",
      label: "Coaches",
      count: coaches.length > 0 ? undefined : 0,
    },
    { id: "articles", label: "Articles", count: articleRows.length },
    { id: "faq", label: "FAQ", count: faqs.length },
  ];

  // Adapt server CTP shape into ProgramRowData (fill unavailable fields with null).
  const programRows: ProgramRowData[] = ctps.map((ctp) => ({
    id: ctp.id,
    slug: ctp.slug,
    name: ctp.name,
    logoUrl: null,
    deliveryFormat: ctp.deliveryFormat,
    accreditationLevel: ctp.accreditationLevel,
    duration: ctp.duration,
    tuitionRange: null,
  }));

  const socialLinks: SocialLink[] = cco.socialLinks
    ? (
        Object.entries(cco.socialLinks) as [
          keyof typeof SOCIAL_ICONS,
          string | undefined,
        ][]
      )
        .filter(
          (entry): entry is [keyof typeof SOCIAL_ICONS, string] => !!entry[1],
        )
        .map(([key, href]) => {
          const { Icon, label } = SOCIAL_ICONS[key];
          return {
            key,
            href,
            label,
            icon: <Icon className="h-4 w-4" />,
          };
        })
    : [];

  return (
    <>
      <CihHero
        breadcrumb={[
          { label: "Home", href: "/" },
          { label: "Organizations", href: "/organizations" },
          { label: cco.name, href: `/organizations/${slug}` },
        ]}
        kicker={typeLabel}
        kickerColorClass="text-purple-700"
        title={cco.name}
        lede={cco.description}
        logoUrl={cco.logoUrl}
        cta={
          <>
            <Link to="/discovery" search={{ source: `cco:${slug}` }}>
              <Button variant="primary">Find a coach →</Button>
            </Link>
            {cco.website && websiteHost ? (
              <ButtonLink href={cco.website} target="_blank" variant="ghost">
                Visit {websiteHost}
                <ExternalLink className="ml-1.5 inline h-4 w-4" />
              </ButtonLink>
            ) : null}
          </>
        }
        meta={<CihAtAGlanceCard rows={atAGlanceRows} socials={socialLinks} />}
      />

      <div className="mx-auto max-w-7xl px-4 pb-6 text-sm text-gray-600 xl:px-0">
        Do you work at {cco.name}?{" "}
        <Link
          to="/organizations/$slug/claim"
          params={{ slug }}
          className="text-purple-700 underline-offset-4 hover:underline"
        >
          Claim this organization →
        </Link>
      </div>

      <CihStatStrip cells={statCells} />
      <CihTabNav tabs={tabs} />

      {cco.body ? (
        <CihSection id="overview" divider={false}>
          <CihSectionHeader label="Overview" title="About" />
          <div className="mx-auto grid max-w-7xl gap-10 px-4 md:grid-cols-[1fr_300px] xl:px-0">
            <div className="prose prose-lg max-w-none">
              <RichTextViewer
                content={JSON.stringify(dropRestatedOpeningHeading(cco.body))}
              />
            </div>
            {cco.differentiator ? (
              <aside className="border-l-2 border-purple-600 pl-5">
                <div className="text-[10px] font-medium tracking-[0.22em] text-purple-700 uppercase">
                  What sets them apart
                </div>
                <p className="mt-2 text-sm leading-relaxed text-gray-700">
                  {cco.differentiator}
                </p>
              </aside>
            ) : null}
          </div>
        </CihSection>
      ) : null}

      {cco.pullQuote ? (
        <div id="mission">
          <CihPullQuoteBand
            quote={cco.pullQuote}
            attribution={cco.pullQuoteAttribution}
          />
        </div>
      ) : null}

      {frameworks.length > 0 ? (
        <CihSection id="frameworks" divider={!cco.pullQuote}>
          <CihFrameworkCards
            frameworks={frameworks}
            sectionLabel="Frameworks"
            heading="Approaches coaches use"
          />
        </CihSection>
      ) : null}

      {ctps.length > 0 ? (
        <CihSection id="programs">
          <CihProgramRows
            programs={programRows}
            sectionLabel="Training programs"
            heading={`${ctps.length} ${cco.name} training programs`}
            seeAllHref={`/organizations/${slug}/training-programs`}
            seeAllCount={ctps.length}
          />
        </CihSection>
      ) : null}

      {challengePills.length > 0 ? (
        <CihSection id="challenges">
          <RelatedEntityPills
            items={challengePills}
            sectionLabel="What they help with"
            heading={`Challenges our ${cco.name} coaches help clients through`}
          />
        </CihSection>
      ) : null}

      {coaches.length > 0 ? (
        <CihSection id="coaches">
          <CihCoachGrid
            coaches={coaches}
            sectionLabel="Coaches"
            heading={`${cco.name} coaches on Dream Coach Match`}
            seeAllHref={`/organizations/${slug}/coaches`}
          />
        </CihSection>
      ) : null}

      {articleRows.length > 0 ? (
        <CihSection id="articles">
          <CihArticleRows
            articles={articleRows}
            sectionLabel="Articles"
            heading={`Recent writing from ${cco.name} coaches`}
            showChallenge
          />
        </CihSection>
      ) : null}

      {faqs.length > 0 ? (
        <CihSection id="faq">
          <CihFaqSection faqs={faqs} heading={`Questions about ${cco.name}`} />
        </CihSection>
      ) : null}

      <CihClosingCta
        headline={`Work with a ${cco.name}-credentialed coach matched to your situation.`}
        sub="Free · 5 minutes · No commitment"
        cta={
          <Link to="/discovery" search={{ source: `cco:${slug}` }}>
            <Button variant="primary" size="lg">
              Start →
            </Button>
          </Link>
        }
      />
    </>
  );
}
